import { useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

const DIALOG_LOOKUP_INTERVAL = 100;

export const usePopupAriaAttributes = ({
  ariaLabel,
  open = true,
}: {
  ariaLabel: string;
  open?: boolean;
}): {
  /**
   * Class name to be applied to the popup content element for accessibility purposes.
   * This class name is generated using a unique ID to ensure it is unique across multiple popups.
   * The popup content element will have the following attributes set:
   * - aria-modal="true"
   * - aria-label={ariaLabel}
   * The attributes are applied by querying the DOM for an element with the generated class name and role="dialog".
   * This is necessary because the underlying reactjs-popup library does not provide a way to set these attributes directly.
   */
  popupClassName: string;
} => {
  const popupId = useMemo(() => `popup-${uuidv4()}`, []);
  useEffect(() => {
    if (open) {
      const intervalId = setInterval(() => {
        const element = document.querySelector(
          `[role="dialog"].${popupId}-content`,
        );
        if (element) {
          clearInterval(intervalId);
          element.setAttribute('aria-modal', 'true');
          element.setAttribute('aria-label', ariaLabel);
        }
      }, DIALOG_LOOKUP_INTERVAL);
      return () => clearInterval(intervalId);
    }
  }, [popupId, ariaLabel, open]);

  return {
    popupClassName: popupId,
  };
};
